import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import {
  ManagementService,
  SensorsSensor,
  SensorsService,
  TrigonosDatasource,
} from 'src/app/api/generated';
import { ReportModulesService } from 'src/app/services/report-modules/report-modules.service';
import {
  AxisConfig,
  Datasource,
  ORTHOPHOTO_FIELDS,
  OrthofotoConfigGeneric,
} from '../orthophoto.tools';
import * as uuid from 'uuid';
import { DataChangedService } from 'src/app/data-changed.service';
import { validTime } from 'src/app/tools/validators';

@Component({
    selector: 'app-orthofoto-generic-config',
    templateUrl: './orthofoto-config.component.html',
    styleUrls: ['./orthofoto-config.component.scss'],
    standalone: false
})
export class GenericOrthofotoConfigComponent implements OnInit {
  public orthofotoForm: UntypedFormGroup;
  public availableDataSources: SensorsSensor[] = [];
  public editMode = false;
  public submitting = false;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrthofotoConfigGeneric,
    private dialogRef: MatDialogRef<OrthofotoConfigGeneric>,
    private formBuilder: UntypedFormBuilder,
    private reportModuleService: ReportModulesService,
    private managementService: ManagementService,
    private dataChangedService: DataChangedService,
    private sensorsService: SensorsService
  ) {}

  private filterSensors(sensors: SensorsSensor[]): SensorsSensor[] {
    return sensors.filter((sensor) => {
      const fields = sensor.available_fields.map((field) => field.identifier);

      return (
        fields.length > 0 &&
        ORTHOPHOTO_FIELDS.every((field) => fields.includes(field))
      );
    });
  }

  ngOnInit(): void {
    this.editMode = this.data?.guid !== undefined;
    this.sensorsService
      .sensorsProjectRead(this.reportModuleService.currentReport.project)
      .subscribe((sensors) => {
        this.availableDataSources = this.filterSensors(sensors);
        const allSelected = this.availableDataSources.every((sensor) =>
          this.fieldHasEnabledSensors(sensor.influx_name)
        );
        this.orthofotoForm.get('allDatasourcesChecked').setValue(allSelected);
        this.availableDataSources.forEach((sensor) => {
          (this.orthofotoForm.get('datasources') as FormArray).push(
            new FormGroup({
              id: new UntypedFormControl(sensor.influx_name),
              name: new UntypedFormControl(sensor.display_name),
              checked: new UntypedFormControl(
                this.fieldHasEnabledSensors(sensor.influx_name)
              ),
            })
          );
        });

        this.datasourcesFromFormGroup().controls.forEach((control) => {
          control.valueChanges.subscribe((value) => {
            const allChecked = this.datasourcesFromFormGroup().controls.every(
              (control) => control.get('checked').value
            );
            if (
              this.orthofotoForm.get('allDatasourcesChecked').value !==
              allChecked
            ) {
              this.orthofotoForm
                .get('allDatasourcesChecked')
                .setValue(allChecked, { emitEvent: false });
            }
          });
        });
      });

    this.orthofotoForm = this.formBuilder.group({
      guid: [this.data?.guid ? this.data.guid : uuid.v4()],
      type: ['orthophoto-generic'],
      reportID: [
        this.reportModuleService.currentReport.id,
        Validators.required,
      ],
      headline: [
        this.data?.headline ? this.data?.headline : '',
        Validators.required,
      ],
      datasources: this.formBuilder.array([]),
      allDatasourcesChecked: [false],
      timePeriodFiltered:
        this.data?.filter.enabled !== undefined
          ? this.data?.filter.enabled
          : false,
      timePeriodFilteredFrom: [
        this.data?.filter.from ? this.data?.filter.from : '22:00',
        [Validators.required, validTime()],
      ],
      timePeriodFilteredTill: [
        this.data?.filter.till ? this.data?.filter.till : '06:00',
        [Validators.required, validTime()],
      ],
      differenceHours: [
        this.data?.dynamic_center_point.difference_hours
          ? this.data?.dynamic_center_point.difference_hours
          : 6,
        [Validators.required],
      ],
      colorOk: [
        this.data?.dynamic_center_point.color_ok
          ? this.data?.dynamic_center_point.color_ok
          : 'blue',
        [Validators.required],
      ],
      colorWarning: [
        this.data?.dynamic_center_point.color_warning
          ? this.data?.dynamic_center_point.color_warning
          : 'red',
        [Validators.required],
      ],
      colorHeightVector: [
        this.data?.color_height_vector
          ? this.data?.color_height_vector
          : 'yellow',
        [Validators.required],
      ],
      color2dVector: [
        this.data?.color_2d_vector ? this.data?.color_2d_vector : 'red',
        [Validators.required],
      ],
      scaleSizeFactor: [
        this.data?.scale_size_factor ? this.data?.scale_size_factor : 100,
        [Validators.required],
      ],
      axis: this.formBuilder.array([]),
    });

    this.orthofotoForm.controls['allDatasourcesChecked'].valueChanges.subscribe(
      (value) => {
        this.datasourcesFromFormGroup().controls.forEach((control) => {
          control.get('checked').setValue(value);
        });
      }
    );

    for (const axis of this.data.axis) {
      this.axisFromFormGroup().push(
        this.formBuilder.group({
          x1: [axis.x1, [Validators.required]],
          y1: [axis.y1, [Validators.required]],
          x2: [axis.x2, [Validators.required]],
          y2: [axis.y2, [Validators.required]],
          text: [axis.text, [Validators.required]],
        })
      );
    }
  }

  public updateOrCreateModule() {
    if (this.orthofotoForm.valid) {
      this.submitting = true;
      const axis: AxisConfig[] = this.axisFromFormGroup().controls.map(
        (control) => {
          return {
            x1: this.toNumber(control.get('x1').value),
            y1: this.toNumber(control.get('y1').value),
            x2: this.toNumber(control.get('x2').value),
            y2: this.toNumber(control.get('y2').value),
            text: control.get('text').value,
          };
        }
      );

      this.reportModuleService
        .addOrEditModuleToReport(
          <OrthofotoConfigGeneric>{
            guid: this.orthofotoForm.value.guid,
            type: this.orthofotoForm.value.type,
            headline: this.orthofotoForm.value.headline,
            datasources: this.orthofotoForm.value.datasources as Datasource[],
            filter: {
              enabled: this.orthofotoForm.value.timePeriodFiltered,
              from: this.orthofotoForm.value.timePeriodFilteredFrom,
              till: this.orthofotoForm.value.timePeriodFilteredTill,
            },
            user_settings: {
              selected_date: null,
            },
            axis: axis || [],
            dynamic_center_point: {
              difference_hours: this.orthofotoForm.value.differenceHours,
              color_ok: this.orthofotoForm.value.colorOk,
              color_warning: this.orthofotoForm.value.colorWarning,
            },
            color_2d_vector: this.orthofotoForm.value.color2dVector,
            color_height_vector: this.orthofotoForm.value.colorHeightVector,
            scale_size_factor: this.orthofotoForm.value.scaleSizeFactor,
          },
          this.editMode
        )
        .subscribe(
          (success) => {
            this.submitting = false;
            this.dialogRef.close();
            this.dataChangedService.configUpdated(
              this.reportModuleService.currentReport.id,
              this.orthofotoForm.value.guid
            );
          },
          (error) => {
            this.submitting = false;
          }
        );
    }
  }

  addAxis(event): void {
    this.axisFromFormGroup().push(
      this.formBuilder.group({
        x1: ['', [Validators.required]],
        y1: ['', [Validators.required]],
        x2: ['', [Validators.required]],
        y2: ['', [Validators.required]],
        text: ['', [Validators.required]],
      })
    );
  }

  get timeFilterDisabled(): string | null {
    return this.orthofotoForm.get('timePeriodFiltered').value ? null : '';
  }

  private datasourcesFromFormGroup(): UntypedFormArray {
    return this.orthofotoForm.get('datasources') as UntypedFormArray;
  }

  public axisFromFormGroup(): UntypedFormArray {
    return this.orthofotoForm.get('axis') as UntypedFormArray;
  }

  private toNumber(value: string | number): number {
    return typeof value === 'number' ? value : Number(value.replace(',', '.'));
  }

  public fieldHasEnabledSensors(field: string): boolean {
    const sensors = this.data?.datasources.filter((f) => f.id === field);
    if (!sensors) {
      return false;
    }

    const enabledSensors = sensors.filter((s) => s.checked);
    return enabledSensors.length > 0;
  }
}
